import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetCustomers,
  CreateCustomer,
  UpdateCustomer,
  DeleteCustomer,
} from "../Api";
import {
  DEFAULT_STALE_TIME,
  LOOKUP_STALE_TIME,
  ServiceQueryOptions,
  unwrapApiResponse,
} from "./query_helpers";

export const customerQueryKeys = {
  customers: () => ["customers"] as const,
};

export const invalidateCustomerQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["customers"] });
};

export const useCustomersQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: customerQueryKeys.customers(),
    queryFn: async () => await GetCustomers(),
  });
};

export const useCustomersRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: LOOKUP_STALE_TIME,
        queryKey: customerQueryKeys.customers(),
        queryFn: async () => await GetCustomers(),
      }),
  });
};

export const useCreateCustomerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateCustomer>[0]) =>
      CreateCustomer(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateCustomerQueries(queryClient);
      }
    },
  });
};

export const useUpdateCustomerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateCustomer>[0]) =>
      UpdateCustomer(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateCustomerQueries(queryClient);
      }
    },
  });
};

export const useDeleteCustomerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => DeleteCustomer(id),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateCustomerQueries(queryClient);
      }
    },
  });
};

export const useCustomerService = (config?: {
  fetchCustomersEnabled?: boolean;
}) => {
  const customersQuery = useCustomersQuery({
    enabled: config?.fetchCustomersEnabled !== false,
  });

  const createCustomerMutation = useCreateCustomerMutation();
  const updateCustomerMutation = useUpdateCustomerMutation();
  const deleteCustomerMutation = useDeleteCustomerMutation();

  return {
    customersQuery,
    createCustomerMutation,
    updateCustomerMutation,
    deleteCustomerMutation,
  };
};
